import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Search, Package, Plus, Minus, Edit, Trash2, Check, X,
  ShoppingCart, Filter, ChevronDown, Tag, Box, RefreshCw,
  Store, MapPin, Phone, Truck, Settings, Globe, Upload, BookOpen,
  Video, Image, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { supermarket } from '@/lib/api';
import { useCurrency } from '@/lib/currency-context';
import { toast } from 'sonner';
import debounce from 'lodash/debounce';
import { worldCountries, getCountryKey, getCityKey, getDistrictKey } from '@/lib/countries';
import ProductImportTab from './ProductImportTab';

// Exchange rates for converting to USD
const exchangeRatesToUSD = {
  'USD': 1,
  'EUR': 1.087,
  'GBP': 1.266,
  'TRY': 0.029,
  'JPY': 0.0067,
  'CNY': 0.138,
  'INR': 0.012,
  'AUD': 0.658,
  'CAD': 0.735,
  'BRL': 0.201,
  'MXN': 0.058,
  'RUB': 0.011,
  'SAR': 0.267,
  'AED': 0.272,
};

const currencyLabels = {
  'USD': 'USD ($)',
  'EUR': 'EUR (€)',
  'GBP': 'GBP (£)',
  'TRY': 'TRY (₺)',
  'JPY': 'JPY (¥)',
  'CNY': 'CNY (¥)',
  'INR': 'INR (₹)',
  'AUD': 'AUD (A$)',
  'CAD': 'CAD (C$)',
  'BRL': 'BRL (R$)',
  'MXN': 'MXN (MX$)',
  'RUB': 'RUB (₽)',
  'SAR': 'SAR (SR)',
  'AED': 'AED',
};

// Available sizes
const availableSizes = [
  '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46',
  'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'
];

// Predefined colors
const predefinedColors = [
  { name: 'Siyah', value: '#000000' },
  { name: 'Beyaz', value: '#FFFFFF' },
  { name: 'Kırmızı', value: '#FF0000' },
  { name: 'Mavi', value: '#0000FF' },
  { name: 'Yeşil', value: '#00FF00' },
  { name: 'Sarı', value: '#FFFF00' },
  { name: 'Turuncu', value: '#FFA500' },
  { name: 'Mor', value: '#800080' },
  { name: 'Pembe', value: '#FFC0CB' },
  { name: 'Kahverengi', value: '#A52A2A' },
  { name: 'Gri', value: '#808080' },
  { name: 'Lacivert', value: '#000080' },
  { name: 'Bej', value: '#F5F5DC' },
  { name: 'Altın', value: '#FFD700' },
  { name: 'Gümüş', value: '#C0C0C0' },
];

// Available countries for product availability
const availableCountriesForProduct = [
  { code: 'ALL', name: 'Tüm Dünya / Worldwide', flag: '🌍' },
  { code: 'TR', name: 'Türkiye', flag: '🇹🇷' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
];

const CATEGORY_KEYS = {
  gida: "basicFood",
  sut_urunleri: "dairyProducts",
  icecekler: "beverages",
  atistirmalik: "snacks",
  temizlik: "cleaningProducts",
  kisisel_bakim: "personalCare",
  bebek: "babyProducts",
  ev_gerecleri: "homeSupplies"
};

const CATEGORY_ICONS = {
  gida: "🍞",
  sut_urunleri: "🥛",
  icecekler: "🥤",
  atistirmalik: "🍫",
  temizlik: "🧹",
  kisisel_bakim: "🧴",
  bebek: "👶",
  ev_gerecleri: "🏠"
};

const SupermarketTab = () => {
  const { t } = useTranslation();
  const { formatPrice, convertPrice } = useCurrency();
  
  const [activeTab, setActiveTab] = useState('catalog');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [categories, setCategories] = useState({});
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCatalog, setTotalCatalog] = useState(0);
  const [page, setPage] = useState(1);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [stockInput, setStockInput] = useState('0');
  const [discountInput, setDiscountInput] = useState('0');
  const [submitting, setSubmitting] = useState(false);

  // Manual product form state
  const [manualProduct, setManualProduct] = useState({
    name: '',
    brand: '',
    description: '',
    category: '',
    subcategory: '',
    unit: 'adet',
    weight: '',
    price: '',
    stock: '0',
    discount_percentage: '0',
    // New fields
    images: [''],
    videos: [''],
    made_in_turkey: false,
    // Physical properties
    weight_value: '',
    weight_unit: 'kg',
    dimensions: { width: '', height: '', depth: '' },
    dimension_unit: 'cm',
    volume: '',
    volume_unit: 'L',
    // Product variants
    sizes: [],
    colors: [],
    customColor: '',
    // Origin country
    origin_country: '',
    // Wholesale pricing tiers
    wholesale_tiers: []
  });
  const [addingManual, setAddingManual] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('TRY');
  const [countryMode, setCountryMode] = useState('all');
  const [selectedCountriesForProduct, setSelectedCountriesForProduct] = useState([]);
  
  // Custom categories state
  const [customCategories, setCustomCategories] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // Convert price from selected currency to USD
  const convertToUSD = (price, currency) => {
    if (!price || !currency) return 0;
    return parseFloat(price) * exchangeRatesToUSD[currency];
  };
  
  // Add new custom category
  const handleAddCustomCategory = () => {
    if (newCategoryName.trim()) {
      const categoryKey = newCategoryName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      if (!customCategories.find(c => c.key === categoryKey)) {
        setCustomCategories([...customCategories, { key: categoryKey, name: newCategoryName.trim() }]);
        handleManualProductChange('category', categoryKey);
      }
      setNewCategoryName('');
      setShowNewCategoryInput(false);
    }
  };

  // Added products set for quick lookup
  const [addedProductIds, setAddedProductIds] = useState(new Set());
  
  // Store settings state
  const [storeSettings, setStoreSettings] = useState({
    store_name: '',
    store_description: '',
    country: '',
    city: '',
    district: '',
    address: '',
    phone: '',
    delivery_available: true,
    minimum_order: 0,
    is_verified: false
  });
  const [availableCities, setAvailableCities] = useState([]);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchMyProducts();
    fetchStoreSettings();
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [selectedCategory, selectedBrand, page]);

  // Update cities when country changes
  useEffect(() => {
    if (storeSettings.country && worldCountries[storeSettings.country]) {
      setAvailableCities(Object.keys(worldCountries[storeSettings.country]));
    } else {
      setAvailableCities([]);
    }
  }, [storeSettings.country]);

  // Update districts when city changes
  useEffect(() => {
    if (storeSettings.country && storeSettings.city && worldCountries[storeSettings.country]?.[storeSettings.city]) {
      setAvailableDistricts(worldCountries[storeSettings.country][storeSettings.city]);
    } else {
      setAvailableDistricts([]);
    }
  }, [storeSettings.country, storeSettings.city]);

  const fetchStoreSettings = async () => {
    try {
      const response = await supermarket.getStoreSettings();
      setStoreSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch store settings:', error);
    }
  };

  const saveStoreSettings = async () => {
    if (!storeSettings.store_name || !storeSettings.country || !storeSettings.city) {
      toast.error(t('sellerPanel.storeRequiredFields'));
      return;
    }
    
    try {
      setSavingSettings(true);
      const response = await supermarket.updateStoreSettings(storeSettings);
      toast.success(t('sellerPanel.settingsSaved'));
      
      if (response.data.is_verified) {
        setStoreSettings(prev => ({ ...prev, is_verified: true }));
        toast.success(t('sellerPanel.storeActivated'));
      }
      
      fetchStoreSettings();
    } catch (error) {
      console.error('Failed to save store settings:', error);
      toast.error(t('sellerPanel.settingsSaveFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  // Get translated names
  const getCountryName = (country) => {
    const key = getCountryKey(country);
    return t(`location.${key}`, { defaultValue: country });
  };

  const getCityName = (city) => {
    const key = getCityKey(city);
    return t(`location.cities.${key}`, { defaultValue: city });
  };

  const getDistrictName = (district) => {
    const key = getDistrictKey(district);
    return t(`location.districts.${key}`, { defaultValue: district });
  };

  const fetchCategories = async () => {
    try {
      const response = await supermarket.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await supermarket.getBrands();
      setBrands(response.data);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const fetchCatalog = async (query = '') => {
    try {
      setLoading(true);
      const response = await supermarket.getCatalog({
        query: query || searchQuery,
        category: selectedCategory || undefined,
        brand: selectedBrand || undefined,
        page,
        limit: 50
      });
      setCatalogProducts(response.data.products);
      setTotalCatalog(response.data.total);
    } catch (error) {
      console.error('Failed to fetch catalog:', error);
      toast.error(t('sellerPanel.catalogLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProducts = async () => {
    try {
      const response = await supermarket.getMyProducts();
      setMyProducts(response.data);
      // Build set of added product IDs
      const ids = new Set(response.data.map(p => p.catalog_product_id));
      setAddedProductIds(ids);
    } catch (error) {
      console.error('Failed to fetch my products:', error);
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      setPage(1);
      fetchCatalog(query);
    }, 300),
    [selectedCategory, selectedBrand]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleAddProduct = (product) => {
    setSelectedProduct(product);
    setPriceInput('');
    setStockInput('0');
    setDiscountInput('0');
    setShowAddDialog(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setPriceInput(product.price.toString());
    setStockInput(product.stock.toString());
    setDiscountInput(product.discount_percentage.toString());
    setShowEditDialog(true);
  };

  const submitAddProduct = async () => {
    if (!priceInput || parseFloat(priceInput) <= 0) {
      toast.error(t('sellerPanel.enterValidPrice'));
      return;
    }

    try {
      setSubmitting(true);
      await supermarket.addProduct({
        catalog_product_id: selectedProduct.id,
        price: parseFloat(priceInput),
        stock: parseInt(stockInput) || 0,
        discount_percentage: parseFloat(discountInput) || 0
      });
      toast.success(t('sellerPanel.productAdded'));
      setShowAddDialog(false);
      fetchMyProducts();
    } catch (error) {
      console.error('Failed to add product:', error);
      toast.error(error.response?.data?.detail || t('sellerPanel.productAddFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitEditProduct = async () => {
    if (!priceInput || parseFloat(priceInput) <= 0) {
      toast.error(t('sellerPanel.enterValidPrice'));
      return;
    }

    try {
      setSubmitting(true);
      await supermarket.updateProduct(selectedProduct.id, {
        price: parseFloat(priceInput),
        stock: parseInt(stockInput) || 0,
        discount_percentage: parseFloat(discountInput) || 0,
        is_available: true
      });
      toast.success(t('sellerPanel.productUpdated'));
      setShowEditDialog(false);
      fetchMyProducts();
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error(t('sellerPanel.productUpdateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm(t('sellerPanel.confirmDeleteProduct'))) {
      return;
    }

    try {
      await supermarket.deleteProduct(productId);
      toast.success(t('sellerPanel.productDeleted'));
      fetchMyProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error(t('sellerPanel.productDeleteFailed'));
    }
  };

  const handleManualProductChange = (field, value) => {
    setManualProduct(prev => ({ ...prev, [field]: value }));
  };

  const handleCountryToggleForProduct = (countryCode) => {
    if (selectedCountriesForProduct.includes(countryCode)) {
      setSelectedCountriesForProduct(selectedCountriesForProduct.filter(c => c !== countryCode));
    } else {
      setSelectedCountriesForProduct([...selectedCountriesForProduct, countryCode]);
    }
  };

  const resetManualForm = () => {
    setManualProduct({
      name: '',
      brand: '',
      description: '',
      category: '',
      subcategory: '',
      unit: 'adet',
      weight: '',
      price: '',
      stock: '0',
      discount_percentage: '0',
      images: [''],
      videos: [''],
      made_in_turkey: false,
      weight_value: '',
      weight_unit: 'kg',
      dimensions: { width: '', height: '', depth: '' },
      dimension_unit: 'cm',
      volume: '',
      volume_unit: 'L',
      sizes: [],
      colors: [],
      customColor: '',
      origin_country: '',
      wholesale_tiers: []
    });
    setSelectedCurrency('TRY');
    setCountryMode('all');
    setSelectedCountriesForProduct([]);
  };

  const submitManualProduct = async () => {
    // Validation
    if (!manualProduct.name || !manualProduct.brand || !manualProduct.category || !manualProduct.price) {
      toast.error(t('sellerPanel.manualRequiredFields'));
      return;
    }

    if (parseFloat(manualProduct.price) <= 0) {
      toast.error(t('sellerPanel.enterValidPrice'));
      return;
    }

    try {
      setAddingManual(true);
      
      // Convert prices to USD
      const priceInUSD = convertToUSD(manualProduct.price, selectedCurrency);
      
      // Determine country availability
      let availableCountriesData = ['ALL'];
      let excludedCountriesData = [];
      
      if (countryMode === 'include' && selectedCountriesForProduct.length > 0) {
        availableCountriesData = selectedCountriesForProduct;
      } else if (countryMode === 'exclude' && selectedCountriesForProduct.length > 0) {
        availableCountriesData = ['ALL'];
        excludedCountriesData = selectedCountriesForProduct;
      }
      
      await supermarket.addManualProduct({
        name: manualProduct.name,
        brand: manualProduct.brand,
        description: manualProduct.description,
        category: manualProduct.category,
        subcategory: manualProduct.subcategory,
        unit: manualProduct.unit,
        weight: manualProduct.weight || null,
        price: priceInUSD,
        original_currency: selectedCurrency,
        original_price: parseFloat(manualProduct.price),
        stock: parseInt(manualProduct.stock) || 0,
        discount_percentage: parseFloat(manualProduct.discount_percentage) || 0,
        // New fields
        images: manualProduct.images.filter(img => img.trim()),
        videos: manualProduct.videos.filter(vid => vid.trim()),
        made_in_turkey: manualProduct.made_in_turkey,
        // Physical properties
        weight_value: manualProduct.weight_value ? parseFloat(manualProduct.weight_value) : null,
        weight_unit: manualProduct.weight_unit,
        dimensions: (manualProduct.dimensions.width || manualProduct.dimensions.height || manualProduct.dimensions.depth) 
          ? {
              width: manualProduct.dimensions.width ? parseFloat(manualProduct.dimensions.width) : null,
              height: manualProduct.dimensions.height ? parseFloat(manualProduct.dimensions.height) : null,
              depth: manualProduct.dimensions.depth ? parseFloat(manualProduct.dimensions.depth) : null
            }
          : null,
        dimension_unit: manualProduct.dimension_unit,
        volume: manualProduct.volume ? parseFloat(manualProduct.volume) : null,
        volume_unit: manualProduct.volume_unit,
        // Product variants
        sizes: manualProduct.sizes,
        colors: manualProduct.colors,
        // Origin country
        origin_country: manualProduct.origin_country || null,
        // Country availability
        available_countries: availableCountriesData,
        excluded_countries: excludedCountriesData,
        // Wholesale pricing tiers
        wholesale_tiers: manualProduct.wholesale_tiers.map(tier => ({
          min_qty: parseInt(tier.min_qty),
          max_qty: tier.max_qty ? parseInt(tier.max_qty) : null,
          price: parseFloat(tier.price),
          unit: tier.unit
        }))
      });
      
      toast.success(t('sellerPanel.productAdded'));
      resetManualForm();
      fetchMyProducts();
      setActiveTab('my-products');
    } catch (error) {
      console.error('Failed to add manual product:', error);
      toast.error(error.response?.data?.detail || t('sellerPanel.productAddFailed'));
    } finally {
      setAddingManual(false);
    }
  };

  const isProductAdded = (catalogProductId) => {
    return addedProductIds.has(catalogProductId);
  };

  const getCategoryStats = () => {
    const stats = {};
    myProducts.forEach(p => {
      stats[p.category] = (stats[p.category] || 0) + 1;
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Package className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('sellerPanel.myProducts')}</p>
                <p className="text-2xl font-bold">{myProducts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Box className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('sellerPanel.catalog')}</p>
                <p className="text-2xl font-bold">{totalCatalog || 210}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Tag className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('sellerPanel.brands')}</p>
                <p className="text-2xl font-bold">{brands.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('sellerPanel.categories')}</p>
                <p className="text-2xl font-bold">{Object.keys(categories).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList className="grid w-full grid-cols-5 max-w-3xl">
            <TabsTrigger value="store-settings" className="flex items-center gap-2">
              <Store className="w-4 h-4" />
              {t('sellerPanel.storeSettings')}
            </TabsTrigger>
            <TabsTrigger value="manual-add" className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {t('sellerPanel.manualAdd')}
            </TabsTrigger>
            <TabsTrigger value="import" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              {t('sellerPanel.productImport')}
            </TabsTrigger>
            <TabsTrigger value="catalog" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              {t('sellerPanel.productCatalog')}
            </TabsTrigger>
            <TabsTrigger value="my-products" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              {t('sellerPanel.myProducts')} ({myProducts.length})
            </TabsTrigger>
          </TabsList>
          <Button variant="outline" asChild className="ml-4">
            <Link to="/seller-guide">
              <BookOpen className="w-4 h-4 mr-2" />
              {t('sellerGuide.title')}
            </Link>
          </Button>
        </div>

        {/* Product Import Tab */}
        <TabsContent value="import" className="mt-4">
          <ProductImportTab onImportComplete={fetchMyProducts} />
        </TabsContent>

        {/* Manual Add Product Tab */}
        <TabsContent value="manual-add" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                {t('sellerPanel.manualAddTitle')}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('sellerPanel.manualAddDesc')}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Product Name & Brand */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellerPanel.productName')} *</Label>
                  <Input
                    value={manualProduct.name}
                    onChange={(e) => handleManualProductChange('name', e.target.value)}
                    placeholder={t('sellerPanel.productNamePlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellerPanel.brandName')} *</Label>
                  <Input
                    value={manualProduct.brand}
                    onChange={(e) => handleManualProductChange('brand', e.target.value)}
                    placeholder={t('sellerPanel.brandNamePlaceholder')}
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>{t('sellerPanel.productDescription')}</Label>
                <Textarea
                  value={manualProduct.description}
                  onChange={(e) => handleManualProductChange('description', e.target.value)}
                  placeholder={t('sellerPanel.productDescPlaceholder')}
                  rows={3}
                />
              </div>

              {/* Category & Subcategory */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellerPanel.category')} *</Label>
                  {!showNewCategoryInput ? (
                    <div className="space-y-2">
                      <Select 
                        value={manualProduct.category} 
                        onValueChange={(value) => {
                          if (value === '__new__') {
                            setShowNewCategoryInput(true);
                          } else {
                            handleManualProductChange('category', value);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('sellerPanel.selectCategory')} />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(CATEGORY_KEYS).map(([key, translationKey]) => (
                            <SelectItem key={key} value={key}>
                              {CATEGORY_ICONS[key]} {t(`sellerPanel.categoryNames.${translationKey}`)}
                            </SelectItem>
                          ))}
                          {customCategories.map((cat) => (
                            <SelectItem key={cat.key} value={cat.key}>
                              📦 {cat.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="__new__" className="text-primary font-medium">
                            ➕ Yeni Kategori Oluştur
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Kategori adı girin..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddCustomCategory();
                            }
                          }}
                        />
                        <Button 
                          type="button" 
                          onClick={handleAddCustomCategory}
                          disabled={!newCategoryName.trim()}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => {
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Yeni kategori adını yazıp Enter&apos;a basın veya ✓ butonuna tıklayın
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('sellerPanel.subcategory')}</Label>
                  <Input
                    value={manualProduct.subcategory}
                    onChange={(e) => handleManualProductChange('subcategory', e.target.value)}
                    placeholder={t('sellerPanel.subcategoryPlaceholder')}
                  />
                </div>
              </div>

              {/* Unit & Weight */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellerPanel.unit')}</Label>
                  <Select 
                    value={manualProduct.unit} 
                    onValueChange={(value) => handleManualProductChange('unit', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adet">{t('sellerPanel.unitPiece')}</SelectItem>
                      <SelectItem value="kg">{t('sellerPanel.unitKg')}</SelectItem>
                      <SelectItem value="litre">{t('sellerPanel.unitLitre')}</SelectItem>
                      <SelectItem value="paket">{t('sellerPanel.unitPackage')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('sellerPanel.weight')}</Label>
                  <Input
                    value={manualProduct.weight}
                    onChange={(e) => handleManualProductChange('weight', e.target.value)}
                    placeholder={t('sellerPanel.weightPlaceholder')}
                  />
                </div>
              </div>

              {/* Price, Stock, Discount */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>{t('sellerPanel.currency')}</Label>
                  <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(currencyLabels).map(([code, label]) => (
                        <SelectItem key={code} value={code}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('sellerPanel.salePrice')} ({selectedCurrency}) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={manualProduct.price}
                    onChange={(e) => handleManualProductChange('price', e.target.value)}
                    placeholder="0.00"
                  />
                  {manualProduct.price && selectedCurrency !== 'USD' && (
                    <p className="text-xs text-muted-foreground">
                      ≈ ${convertToUSD(manualProduct.price, selectedCurrency).toFixed(2)} USD
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('sellerPanel.stockQuantity')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={manualProduct.stock}
                    onChange={(e) => handleManualProductChange('stock', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('sellerPanel.discountPercent')}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="99"
                    value={manualProduct.discount_percentage}
                    onChange={(e) => handleManualProductChange('discount_percentage', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-600" />
                  <Label className="text-base font-medium">{t('sellerPanel.productImage')}</Label>
                </div>
                
                {/* File Upload */}
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Bilgisayarınızdan yükleyin</Label>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
                            return;
                          }
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/upload-product-image`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              },
                              body: formData
                            });
                            if (response.ok) {
                              const data = await response.json();
                              setManualProduct({...manualProduct, images: [data.url]});
                              toast.success('Görsel yüklendi!');
                            } else {
                              const error = await response.json();
                              toast.error(error.detail || 'Görsel yüklenemedi');
                            }
                          } catch (error) {
                            console.error('Upload error:', error);
                            toast.error('Görsel yüklenirken hata oluştu');
                          }
                        }
                      }}
                      className="flex-1"
                    />
                  </div>
                </div>
                
                {/* URL Input */}
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">veya URL yapıştırın</Label>
                  <Input
                    value={manualProduct.images[0]}
                    onChange={(e) => setManualProduct({...manualProduct, images: [e.target.value]})}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                {/* Image Preview */}
                {manualProduct.images[0] && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground mb-2">Önizleme:</p>
                    <div className="relative w-32 h-32">
                      <img
                        src={manualProduct.images[0]}
                        alt="Preview"
                        className="w-full h-full object-cover rounded-lg border"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <button
                        type="button"
                        onClick={() => setManualProduct({...manualProduct, images: ['']})}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Video URL Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-purple-600" />
                  <Label className="text-base font-medium">{t('sellerPanel.productVideo')}</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  YouTube veya Vimeo bağlantısı ekleyin
                </p>
                <Input
                  value={manualProduct.videos[0]}
                  onChange={(e) => setManualProduct({...manualProduct, videos: [e.target.value]})}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>

              {/* Made in Türkiye Badge */}
              <div className="border rounded-lg p-4 space-y-3 bg-red-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_emarket-turkey-1/artifacts/dupde9ke_madeinturkiye%20logo%20.png"
                      alt="Made in Türkiye"
                      className="w-10 h-10 object-contain"
                    />
                    <div>
                      <Label className="text-base font-medium">Made in Türkiye Rozeti</Label>
                      <p className="text-sm text-muted-foreground">Bu ürün Türkiyede üretildi mi?</p>
                    </div>
                  </div>
                  <Switch
                    checked={manualProduct.made_in_turkey}
                    onCheckedChange={(checked) => handleManualProductChange('made_in_turkey', checked)}
                  />
                </div>
                {manualProduct.made_in_turkey && (
                  <p className="text-xs text-green-700 bg-green-100 p-2 rounded">
                    ✓ Bu ürün Made in Türkiye rozeti ile gösterilecek
                  </p>
                )}
              </div>

              {/* Country Availability Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <Label className="text-base font-medium">{t('sellerPanel.countryAvailability') || 'Ülke Kullanılabilirliği'}</Label>
                </div>
                
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={countryMode === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setCountryMode('all'); setSelectedCountriesForProduct([]); }}
                      className="flex-1"
                    >
                      🌍 Tüm Dünya
                    </Button>
                    <Button
                      type="button"
                      variant={countryMode === 'include' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCountryMode('include')}
                      className="flex-1"
                    >
                      ✅ Seçili Ülkeler
                    </Button>
                    <Button
                      type="button"
                      variant={countryMode === 'exclude' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCountryMode('exclude')}
                      className="flex-1"
                    >
                      ❌ Hariç Tut
                    </Button>
                  </div>

                  {countryMode !== 'all' && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {countryMode === 'include' 
                          ? 'Ürünün satılacağı ülkeleri seçin'
                          : 'Ürünün satılmayacağı ülkeleri seçin'}
                      </p>
                      
                      {selectedCountriesForProduct.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {selectedCountriesForProduct.map(code => {
                            const country = availableCountriesForProduct.find(c => c.code === code);
                            return (
                              <Badge 
                                key={code} 
                                variant="secondary" 
                                className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                                onClick={() => handleCountryToggleForProduct(code)}
                              >
                                {country?.flag} {country?.name} <X className="w-3 h-3 ml-1" />
                              </Badge>
                            );
                          })}
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                        {availableCountriesForProduct.filter(c => c.code !== 'ALL').map((country) => (
                          <div
                            key={country.code}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-secondary transition-colors ${
                              selectedCountriesForProduct.includes(country.code) 
                                ? 'bg-primary/10 border border-primary' 
                                : 'bg-secondary/30'
                            }`}
                            onClick={() => handleCountryToggleForProduct(country.code)}
                          >
                            <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                              selectedCountriesForProduct.includes(country.code)
                                ? 'bg-primary border-primary'
                                : 'border-gray-300'
                            }`}>
                              {selectedCountriesForProduct.includes(country.code) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span className="text-lg">{country.flag}</span>
                            <span className="text-sm truncate">{country.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {countryMode === 'all' && (
                    <p className="text-sm text-green-600 bg-green-50 p-2 rounded">
                      ✅ Ürün tüm dünyada satışa sunulacak
                    </p>
                  )}
                </div>
              </div>

              {/* Physical Properties Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <Label className="text-base font-medium">📦 Fiziksel Özellikler</Label>
                <p className="text-sm text-muted-foreground">Kargo hesaplaması için gerekli olabilir</p>
                
                {/* Weight */}
                <div className="grid md:grid-cols-4 gap-2">
                  <div className="md:col-span-3">
                    <Label>Ağırlık</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualProduct.weight_value}
                      onChange={(e) => handleManualProductChange('weight_value', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Birim</Label>
                    <Select 
                      value={manualProduct.weight_unit}
                      onValueChange={(value) => handleManualProductChange('weight_unit', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="lb">lb</SelectItem>
                        <SelectItem value="oz">oz</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Dimensions */}
                <div>
                  <Label>Boyutlar</Label>
                  <div className="grid md:grid-cols-4 gap-2 mt-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={manualProduct.dimensions.width}
                      onChange={(e) => setManualProduct({ 
                        ...manualProduct, 
                        dimensions: { ...manualProduct.dimensions, width: e.target.value }
                      })}
                      placeholder="Genişlik"
                    />
                    <Input
                      type="number"
                      step="0.1"
                      value={manualProduct.dimensions.height}
                      onChange={(e) => setManualProduct({ 
                        ...manualProduct, 
                        dimensions: { ...manualProduct.dimensions, height: e.target.value }
                      })}
                      placeholder="Yükseklik"
                    />
                    <Input
                      type="number"
                      step="0.1"
                      value={manualProduct.dimensions.depth}
                      onChange={(e) => setManualProduct({ 
                        ...manualProduct, 
                        dimensions: { ...manualProduct.dimensions, depth: e.target.value }
                      })}
                      placeholder="Derinlik"
                    />
                    <Select 
                      value={manualProduct.dimension_unit}
                      onValueChange={(value) => handleManualProductChange('dimension_unit', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="inch">inch</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Volume */}
                <div className="grid md:grid-cols-4 gap-2">
                  <div className="md:col-span-3">
                    <Label>Hacim</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualProduct.volume}
                      onChange={(e) => handleManualProductChange('volume', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Birim</Label>
                    <Select 
                      value={manualProduct.volume_unit}
                      onValueChange={(value) => handleManualProductChange('volume_unit', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L">L</SelectItem>
                        <SelectItem value="mL">mL</SelectItem>
                        <SelectItem value="m³">m³</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Product Variants Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <Label className="text-base font-medium">👕 Ürün Varyantları</Label>
                <p className="text-sm text-muted-foreground">Beden ve renk seçenekleri (varsa)</p>
                
                {/* Sizes */}
                <div>
                  <Label>Bedenler</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-secondary/20 rounded-lg mt-2">
                    {availableSizes.map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          if (manualProduct.sizes.includes(size)) {
                            setManualProduct({ 
                              ...manualProduct, 
                              sizes: manualProduct.sizes.filter(s => s !== size) 
                            });
                          } else {
                            setManualProduct({ 
                              ...manualProduct, 
                              sizes: [...manualProduct.sizes, size] 
                            });
                          }
                        }}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          manualProduct.sizes.includes(size)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background hover:bg-secondary'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  {manualProduct.sizes.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Seçili: {manualProduct.sizes.join(', ')}
                    </p>
                  )}
                </div>

                {/* Colors */}
                <div>
                  <Label>Renkler</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-secondary/20 rounded-lg mt-2">
                    {predefinedColors.map(color => (
                      <button
                        key={color.name}
                        type="button"
                        onClick={() => {
                          const colorValue = `${color.name} (${color.value})`;
                          if (manualProduct.colors.includes(colorValue)) {
                            setManualProduct({ 
                              ...manualProduct, 
                              colors: manualProduct.colors.filter(c => c !== colorValue) 
                            });
                          } else {
                            setManualProduct({ 
                              ...manualProduct, 
                              colors: [...manualProduct.colors, colorValue] 
                            });
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors border-2 ${
                          manualProduct.colors.some(c => c.startsWith(color.name))
                            ? 'border-primary bg-primary/10'
                            : 'border-transparent bg-background hover:bg-secondary'
                        }`}
                      >
                        <div 
                          className="w-4 h-4 rounded-full border border-gray-300" 
                          style={{ backgroundColor: color.value }}
                        />
                        {color.name}
                      </button>
                    ))}
                  </div>
                  
                  {/* Custom Color Input */}
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={manualProduct.customColor}
                      onChange={(e) => handleManualProductChange('customColor', e.target.value)}
                      placeholder="Özel renk ekle..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (manualProduct.customColor.trim()) {
                          setManualProduct({
                            ...manualProduct,
                            colors: [...manualProduct.colors, manualProduct.customColor.trim()],
                            customColor: ''
                          });
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {manualProduct.colors.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {manualProduct.colors.map((color, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-secondary rounded-md text-xs"
                        >
                          {color}
                          <button
                            type="button"
                            onClick={() => {
                              setManualProduct({
                                ...manualProduct,
                                colors: manualProduct.colors.filter((_, i) => i !== index)
                              });
                            }}
                            className="hover:text-destructive"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Origin Country Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-base font-medium">🏭 Menşe Ülkesi</Label>
                <p className="text-sm text-muted-foreground">Ürünün üretildiği ülke</p>
                <Select 
                  value={manualProduct.origin_country}
                  onValueChange={(value) => handleManualProductChange('origin_country', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ülke seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {availableCountriesForProduct.filter(c => c.code !== 'ALL').map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Wholesale Pricing Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">💰 Toptan Fiyatlandırma</Label>
                    <p className="text-sm text-muted-foreground">Miktara göre indirimli fiyatlar</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setManualProduct({
                        ...manualProduct,
                        wholesale_tiers: [...manualProduct.wholesale_tiers, { min_qty: '', max_qty: '', price: '', unit: 'adet' }]
                      });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Kademe Ekle
                  </Button>
                </div>

                {manualProduct.wholesale_tiers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4 bg-secondary/20 rounded-lg">
                    Henüz toptan fiyat kademesi eklenmedi
                  </p>
                ) : (
                  <div className="space-y-3">
                    {manualProduct.wholesale_tiers.map((tier, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-secondary/20 rounded-lg">
                        <div className="col-span-3">
                          <Label className="text-xs">Min. Adet</Label>
                          <Input
                            type="number"
                            min="1"
                            value={tier.min_qty}
                            onChange={(e) => {
                              const newTiers = [...manualProduct.wholesale_tiers];
                              newTiers[index].min_qty = e.target.value;
                              setManualProduct({ ...manualProduct, wholesale_tiers: newTiers });
                            }}
                            placeholder="10"
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Max. Adet</Label>
                          <Input
                            type="number"
                            min="1"
                            value={tier.max_qty}
                            onChange={(e) => {
                              const newTiers = [...manualProduct.wholesale_tiers];
                              newTiers[index].max_qty = e.target.value;
                              setManualProduct({ ...manualProduct, wholesale_tiers: newTiers });
                            }}
                            placeholder="Sınırsız"
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Birim Fiyat ({selectedCurrency})</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={tier.price}
                            onChange={(e) => {
                              const newTiers = [...manualProduct.wholesale_tiers];
                              newTiers[index].price = e.target.value;
                              setManualProduct({ ...manualProduct, wholesale_tiers: newTiers });
                            }}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Birim</Label>
                          <Select 
                            value={tier.unit || 'adet'}
                            onValueChange={(value) => {
                              const newTiers = [...manualProduct.wholesale_tiers];
                              newTiers[index].unit = value;
                              setManualProduct({ ...manualProduct, wholesale_tiers: newTiers });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="adet">Adet</SelectItem>
                              <SelectItem value="kg">kg</SelectItem>
                              <SelectItem value="koli">Koli</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            onClick={() => {
                              const newTiers = manualProduct.wholesale_tiers.filter((_, i) => i !== index);
                              setManualProduct({ ...manualProduct, wholesale_tiers: newTiers });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={resetManualForm}
                  disabled={addingManual}
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('sellerPanel.clearForm')}
                </Button>
                <Button 
                  onClick={submitManualProduct}
                  disabled={addingManual}
                  className="flex-1"
                >
                  {addingManual ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {t('sellerPanel.adding')}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('sellerPanel.addProduct')}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Store Settings Tab */}
        <TabsContent value="store-settings" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {t('sellerPanel.storeInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Verification Status */}
              {storeSettings.is_verified ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">{t('sellerPanel.storeActive')}</p>
                    <p className="text-sm text-green-600">{t('sellerPanel.storeActiveDesc')}</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                  <Store className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">{t('sellerPanel.activateStore')}</p>
                    <p className="text-sm text-amber-600">{t('sellerPanel.activateStoreDesc')}</p>
                  </div>
                </div>
              )}

              {/* Store Name */}
              <div className="space-y-2">
                <Label>{t('sellerPanel.storeName')} *</Label>
                <Input
                  value={storeSettings.store_name}
                  onChange={(e) => setStoreSettings(prev => ({ ...prev, store_name: e.target.value }))}
                  placeholder={t('sellerPanel.storeNamePlaceholder')}
                />
              </div>

              {/* Store Description */}
              <div className="space-y-2">
                <Label>{t('sellerPanel.storeDescription')}</Label>
                <Textarea
                  value={storeSettings.store_description}
                  onChange={(e) => setStoreSettings(prev => ({ ...prev, store_description: e.target.value }))}
                  placeholder={t('sellerPanel.storeDescriptionPlaceholder')}
                  rows={3}
                />
              </div>

              {/* Location Section */}
              <div className="space-y-4">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t('sellerPanel.locationInfo')}
                </Label>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Country */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">{t('sellerPanel.country')} *</Label>
                    <Select 
                      value={storeSettings.country} 
                      onValueChange={(value) => setStoreSettings(prev => ({ 
                        ...prev, 
                        country: value, 
                        city: '', 
                        district: '' 
                      }))}
                    >
                      <SelectTrigger>
                        <Globe className="w-4 h-4 mr-2" />
                        <SelectValue placeholder={t('sellerPanel.selectCountry')} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(worldCountries).map((country) => (
                          <SelectItem key={country} value={country}>
                            {getCountryName(country)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* City */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">{t('sellerPanel.city')} *</Label>
                    <Select 
                      value={storeSettings.city} 
                      onValueChange={(value) => setStoreSettings(prev => ({ 
                        ...prev, 
                        city: value, 
                        district: '' 
                      }))}
                      disabled={!storeSettings.country}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('sellerPanel.selectCity')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {getCityName(city)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* District */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">{t('sellerPanel.district')}</Label>
                    <Select 
                      value={storeSettings.district} 
                      onValueChange={(value) => setStoreSettings(prev => ({ ...prev, district: value }))}
                      disabled={!storeSettings.city}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('sellerPanel.selectDistrict')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDistricts.map((district) => (
                          <SelectItem key={district} value={district}>
                            {getDistrictName(district)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label>{t('sellerPanel.detailedAddress')}</Label>
                <Textarea
                  value={storeSettings.address}
                  onChange={(e) => setStoreSettings(prev => ({ ...prev, address: e.target.value }))}
                  placeholder={t('sellerPanel.addressPlaceholder')}
                  rows={2}
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {t('sellerPanel.phoneNumber')}
                </Label>
                <Input
                  value={storeSettings.phone}
                  onChange={(e) => setStoreSettings(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={t('sellerPanel.phonePlaceholder')}
                />
              </div>

              {/* Delivery Settings */}
              <div className="space-y-4 pt-4 border-t">
                <Label className="flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  {t('sellerPanel.deliverySettings')}
                </Label>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('sellerPanel.deliveryService')}</p>
                    <p className="text-sm text-muted-foreground">{t('sellerPanel.deliveryServiceDesc')}</p>
                  </div>
                  <Switch
                    checked={storeSettings.delivery_available}
                    onCheckedChange={(checked) => setStoreSettings(prev => ({ ...prev, delivery_available: checked }))}
                  />
                </div>

                {storeSettings.delivery_available && (
                  <div className="space-y-2">
                    <Label>{t('sellerPanel.minimumOrder')} (TRY)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={storeSettings.minimum_order}
                      onChange={(e) => setStoreSettings(prev => ({ ...prev, minimum_order: parseFloat(e.target.value) || 0 }))}
                      placeholder="0"
                    />
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="pt-4">
                <Button 
                  onClick={saveStoreSettings} 
                  disabled={savingSettings}
                  className="w-full md:w-auto"
                >
                  {savingSettings ? t('sellerPanel.saving') : t('sellerPanel.saveSettings')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Catalog Tab */}
        <TabsContent value="catalog" className="space-y-4 mt-4">
          {/* Search and Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t('sellerPanel.searchProducts')}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-10"
                  />
                </div>
                <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder={t('sellerPanel.categories')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('sellerPanel.allCategories')}</SelectItem>
                    {Object.entries(CATEGORY_KEYS).map(([key, translationKey]) => (
                      <SelectItem key={key} value={key}>
                        {CATEGORY_ICONS[key]} {t(`sellerPanel.categoryNames.${translationKey}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedBrand} onValueChange={(v) => { setSelectedBrand(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder={t('sellerPanel.brands')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('sellerPanel.allBrands')}</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Catalog Results */}
          <div className="text-sm text-muted-foreground mb-2">
            {totalCatalog} {t('sellerPanel.productsFound')}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2 mb-4"></div>
                    <div className="h-10 bg-muted rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {catalogProducts.map(product => {
                const added = isProductAdded(product.id);
                return (
                  <Card key={product.id} className={`transition-all ${added ? 'border-green-300 bg-green-50/50' : 'hover:shadow-md'}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium line-clamp-1">{product.name}</h4>
                          <p className="text-sm text-muted-foreground">{product.brand}</p>
                        </div>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {CATEGORY_ICONS[product.category]} {product.weight}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[2.5rem]">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs">
                          {t(`sellerPanel.categoryNames.${CATEGORY_KEYS[product.category]}`)}
                        </Badge>
                        {added ? (
                          <Badge className="bg-green-500">
                            <Check className="w-3 h-3 mr-1" />
                            {t('sellerPanel.added')}
                          </Badge>
                        ) : (
                          <Button size="sm" onClick={() => handleAddProduct(product)}>
                            <Plus className="w-4 h-4 mr-1" />
                            {t('sellerPanel.add')}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalCatalog > 50 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button 
                variant="outline" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                {t('sellerPanel.previous')}
              </Button>
              <span className="flex items-center px-4">
                {t('sellerPanel.page')} {page} / {Math.ceil(totalCatalog / 50)}
              </span>
              <Button 
                variant="outline"
                disabled={page >= Math.ceil(totalCatalog / 50)}
                onClick={() => setPage(p => p + 1)}
              >
                {t('sellerPanel.next')}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* My Products Tab */}
        <TabsContent value="my-products" className="space-y-4 mt-4">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge 
              variant={!selectedCategory ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setSelectedCategory('')}
            >
              {t('sellerPanel.all')} ({myProducts.length})
            </Badge>
            {Object.entries(categoryStats).map(([cat, count]) => (
              <Badge 
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedCategory(cat)}
              >
                {CATEGORY_ICONS[cat]} {t(`sellerPanel.categoryNames.${CATEGORY_KEYS[cat]}`)} ({count})
              </Badge>
            ))}
          </div>

          {myProducts.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">{t('sellerPanel.noProducts')}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('sellerPanel.noProductsDesc')}
                </p>
                <Button onClick={() => setActiveTab('catalog')}>
                  <Search className="w-4 h-4 mr-2" />
                  {t('sellerPanel.browseProducts')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myProducts
                .filter(p => !selectedCategory || p.category === selectedCategory)
                .map(product => (
                  <Card key={product.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium line-clamp-1">{product.name}</h4>
                          <p className="text-sm text-muted-foreground">{product.brand}</p>
                        </div>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {product.weight}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-2 my-3">
                        <span className="text-xl font-bold text-primary">
                          {formatPrice(convertPrice(product.price))}
                        </span>
                        {product.discount_percentage > 0 && (
                          <Badge className="bg-red-500">
                            -%{product.discount_percentage}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                        <span>{t('sellerPanel.stock')}: {product.stock}</span>
                        <Badge variant={product.is_available ? 'secondary' : 'destructive'}>
                          {product.is_available ? t('sellerPanel.onSale') : t('sellerPanel.notOnSale')}
                        </Badge>
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleEditProduct(product)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {t('common.edit')}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Product Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sellerPanel.addProduct')}</DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-semibold">{selectedProduct.name}</h4>
                <p className="text-sm text-muted-foreground">{selectedProduct.brand} - {selectedProduct.weight}</p>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                  {selectedProduct.description}
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('sellerPanel.salePrice')} (TRY) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    placeholder={t('sellerPanel.pricePlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('sellerPanel.stockQuantity')}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={stockInput}
                      onChange={(e) => setStockInput(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellerPanel.discountPercent')}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="99"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitAddProduct} disabled={submitting}>
              {submitting ? t('sellerPanel.adding') : t('sellerPanel.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sellerPanel.editProduct')}</DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-semibold">{selectedProduct.name}</h4>
                <p className="text-sm text-muted-foreground">{selectedProduct.brand}</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('sellerPanel.salePrice')} (TRY) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('sellerPanel.stockQuantity')}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={stockInput}
                      onChange={(e) => setStockInput(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellerPanel.discountPercent')}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="99"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitEditProduct} disabled={submitting}>
              {submitting ? t('sellerPanel.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupermarketTab;
